import type { ApiError } from './errors'
import type {
  AuthResponse,
  Candidate,
  CheckEmailExistsRequest,
  CheckEmailExistsResponse,
  CreateAccountFromInterviewRequest,
  CreateAccountFromInterviewResponse,
  CreateAccountFromInterviewSignInResponse,
  EmailCodeRequestRequest,
  EmailCodeRequestResponse,
  EmailCodeVerifyRequest,
  EmailCodeVerifyResponse,
  HiringManager,
  LinkedInStartRequest,
  LinkedInStartResponse,
  LoginRequest,
  MagicLinkRequestRequest,
  MagicLinkRequestResponse,
  MagicLinkVerifyResponse,
  PasswordResetConfirmRequest,
  PasswordResetConfirmResponse,
  PasswordResetRequestRequest,
  PasswordResetRequestResponse,
  PasswordResetValidateResponse,
  RequestAccountCreationLinkRequest,
  RequestAccountCreationLinkResponse,
  ResendVerificationEmailResponse,
  SignupRequest,
  UpdateCandidateProfileRequest,
  UpdateHiringManagerProfileRequest,
  VerifyEmailResponse,
} from './types/generated'

const API_BASE = ''

export class NotAuthenticatedError extends Error implements ApiError {
  readonly status: number
  constructor(status: number) {
    super('Not authenticated')
    this.name = 'NotAuthenticatedError'
    this.status = status
  }
}

export class AuthApiError extends Error implements ApiError {
  readonly status: number
  readonly code: string | undefined
  constructor(status: number, message: string, code?: string) {
    super(message)
    this.name = 'AuthApiError'
    this.status = status
    this.code = code
  }
}

function authApiError(
  body: { error?: string; code?: string },
  status: number,
  fallback: string
): AuthApiError {
  return new AuthApiError(status, body?.error || fallback, body?.code)
}

export const authApi = {
  candidateSignup: async (data: SignupRequest): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE}/api/candidate/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Signup failed')
    }
    return response.json()
  },

  candidateLogin: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE}/api/candidate/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Login failed')
    }
    return response.json()
  },

  hiringManagerSignup: async (data: SignupRequest): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE}/api/hiring-manager/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Signup failed')
    }
    return response.json()
  },

  hiringManagerLogin: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE}/api/hiring-manager/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Login failed')
    }
    return response.json()
  },

  logout: async (): Promise<void> => {
    const response = await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Logout failed')
    }
  },

  getCurrentUser: async (): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/me`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!response.ok) {
      throw new NotAuthenticatedError(response.status)
    }
    return response.json()
  },

  updateCandidateProfile: async (data: UpdateCandidateProfileRequest): Promise<Candidate> => {
    const response = await fetch(`${API_BASE}/api/candidate/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to update profile')
    }
    return response.json()
  },

  linkedInStart: async (data?: LinkedInStartRequest): Promise<LinkedInStartResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/linkedin/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data || {}),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to start LinkedIn authentication')
    }
    return response.json()
  },

  requestPasswordReset: async (
    data: PasswordResetRequestRequest
  ): Promise<PasswordResetRequestResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/password-reset/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to request password reset')
    }
    return response.json()
  },

  validatePasswordResetToken: async (token: string): Promise<PasswordResetValidateResponse> => {
    const response = await fetch(
      `${API_BASE}/api/auth/password-reset/validate/${encodeURIComponent(token)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    )
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to validate token')
    }
    return response.json()
  },

  resetPassword: async (
    data: PasswordResetConfirmRequest
  ): Promise<PasswordResetConfirmResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/password-reset/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to reset password')
    }
    return response.json()
  },

  requestMagicLink: async (data: MagicLinkRequestRequest): Promise<MagicLinkRequestResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/magic-link/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to request magic link')
    }
    return response.json()
  },

  verifyMagicLink: async (token: string): Promise<MagicLinkVerifyResponse> => {
    const response = await fetch(
      `${API_BASE}/api/auth/magic-link/verify/${encodeURIComponent(token)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    )
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to verify magic link')
    }
    return response.json()
  },

  requestEmailCode: async (data: EmailCodeRequestRequest): Promise<EmailCodeRequestResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/email-code/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw authApiError(error, response.status, 'Failed to send code')
    }
    return response.json()
  },

  verifyEmailCode: async (data: EmailCodeVerifyRequest): Promise<EmailCodeVerifyResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/email-code/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw authApiError(error, response.status, 'Failed to verify code')
    }
    return response.json()
  },

  getHiringManagerProfile: async (): Promise<HiringManager> => {
    const response = await fetch(`${API_BASE}/api/hiring-manager/profile`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to get profile')
    }
    return response.json()
  },

  updateHiringManagerProfile: async (
    data: UpdateHiringManagerProfileRequest
  ): Promise<HiringManager> => {
    const response = await fetch(`${API_BASE}/api/hiring-manager/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to update profile')
    }
    return response.json()
  },

  resendVerificationEmail: async (): Promise<ResendVerificationEmailResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/verify-email/resend`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to resend verification email')
    }
    return response.json()
  },

  verifyEmail: async (token: string): Promise<VerifyEmailResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/verify-email/${encodeURIComponent(token)}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to verify email')
    }
    return response.json()
  },

  checkEmailExists: async (data: CheckEmailExistsRequest): Promise<CheckEmailExistsResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/check-email-exists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to check email')
    }
    return response.json()
  },

  checkHiringManagerEmailExists: async (
    data: CheckEmailExistsRequest
  ): Promise<CheckEmailExistsResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/check-hm-email-exists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to check email')
    }
    return response.json()
  },

  requestAccountCreationLink: async (
    data: RequestAccountCreationLinkRequest
  ): Promise<RequestAccountCreationLinkResponse> => {
    const response = await fetch(`${API_BASE}/api/interview/request-account-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to request account creation link')
    }
    return response.json()
  },

  createAccountFromInterview: async (
    data: CreateAccountFromInterviewRequest
  ): Promise<CreateAccountFromInterviewResponse | CreateAccountFromInterviewSignInResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/create-account-from-interview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to create account')
    }
    return response.json()
  },

  requestLoginLinkForExistingUser: async (
    email: string,
    shareToken?: string
  ): Promise<MagicLinkRequestResponse> => {
    const response = await fetch(`${API_BASE}/api/auth/request-login-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        share_token: shareToken,
        redirect_url: '/candidate/dashboard?tab=applications',
      }),
      credentials: 'include',
    })
    if (!response.ok) {
      const error = await response.json()
      throw authApiError(error, response.status, 'Failed to request login link')
    }
    return response.json()
  },
}
